import React, { useEffect, useState, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';
import { PdfViewer } from '../components/PdfViewer';

const eur = (v: any) =>
  Number(v || 0).toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (s: any) => { // [PATCH placila-filter v1] sl-SI ločila tisočic ("3.639,39" -> 3639.39)
  let t = String(s ?? '').replace(/\s/g, '');
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  return parseFloat(t) || 0;
};
const danes = () => new Date().toISOString().slice(0, 10);
const fmtDatum = (s: any) => {
  if (!s) return '—';
  const m = String(s).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? m[3] + '.' + m[2] + '.' + m[1] : String(s);
};

// [PATCH placila-filter v2] pametni vnos datuma — logika parseDatumVnos je 1:1 kopija iz DokumentiPage
// ("12" -> 12.{tekoči mesec}.{leto}, "1806" -> 18.06.{leto}, "180626"/"18062026" polno; ločila . , / presledek)
function parseDatumVnos(vnos: string): string | null {
  if (!vnos) return null;
  const danes = new Date();
  const letoFull = (y: string): number => {
    const n = parseInt(y, 10);
    if (y.length === 4) return n;
    if (y.length <= 2) return 2000 + n;  // "26" -> 2026
    return n;
  };
  let d: number, m: number, y: number;
  // ce ima locilo (. , presledek /)
  const cisto = vnos.trim().replace(/[,\/\s]+/g, '.');
  if (cisto.includes('.')) {
    const p = cisto.split('.').filter(x => x !== '');
    if (p.length < 2) {
      d = parseInt(p[0], 10); m = danes.getMonth() + 1; y = danes.getFullYear();
    } else {
      d = parseInt(p[0], 10);
      m = parseInt(p[1], 10);
      y = p[2] ? letoFull(p[2]) : danes.getFullYear();
    }
  } else {
    // samo stevke
    const num = vnos.replace(/\D/g, '');
    if (num.length <= 2) {            // "18" -> dan
      d = parseInt(num, 10); m = danes.getMonth() + 1; y = danes.getFullYear();
    } else if (num.length === 3) {
      d = parseInt(num.slice(0, 1), 10); m = parseInt(num.slice(1, 3), 10); y = danes.getFullYear();
    } else if (num.length === 4) {    // "1806" -> 18.06.{leto}
      d = parseInt(num.slice(0, 2), 10); m = parseInt(num.slice(2, 4), 10); y = danes.getFullYear();
    } else if (num.length === 6) {    // "180626" -> 18.06.2026
      d = parseInt(num.slice(0, 2), 10); m = parseInt(num.slice(2, 4), 10); y = letoFull(num.slice(4, 6));
    } else if (num.length === 8) {    // "18062026"
      d = parseInt(num.slice(0, 2), 10); m = parseInt(num.slice(2, 4), 10); y = parseInt(num.slice(4, 8), 10);
    } else {
      return null;
    }
  }
  if (!d || !m || d < 1 || d > 31 || m < 1 || m > 12) return null;
  const mm = String(m).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  return `${y}-${mm}-${dd}`;  // interni format
}

function DatumVnos({ value, onChange, className, zahtevan }: {
  value: string; onChange: (v: string) => void; className?: string; zahtevan?: boolean;
}) {
  const displayVal = value ? value.split('-').reverse().join('.') : '';
  const [lokalni, setLokalni] = useState<string | null>(null);
  const prikaz = lokalni !== null ? lokalni : displayVal;
  const potrdi = () => {
    if (lokalni === null) return;
    if (lokalni.trim() === '') { onChange(''); setLokalni(null); return; }
    const parsano = parseDatumVnos(lokalni);
    if (parsano) onChange(parsano);
    setLokalni(null);
  };
  return (
    <input type="text" value={prikaz}
      onChange={(e) => setLokalni(e.target.value)}
      onBlur={potrdi}
      onKeyDown={(e) => { if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); } }}
      className={(className || '') + (zahtevan && !value ? ' ring-1 ring-red-500' : '')} />
  );
}

export function PlacilaPage() {
  const selectedCompany = useAuthStore((s: any) => s.selectedCompany);
  const baza = selectedCompany;
  const [racuni, setRacuni] = useState<any[]>([]);
  const [zavihek, setZavihek] = useState<'neplacani' | 'placani'>('neplacani');
  const [upn, setUpn] = useState<any>(null);
  const [ibani, setIbani] = useState<string[]>([]);
  const [izbranIban, setIzbranIban] = useState('');
  const [urejamIbane, setUrejamIbane] = useState(false);
  const [novIban, setNovIban] = useState('');
  const [vnosi, setVnosi] = useState<any>({});
  const [odprtaZgodovina, setOdprtaZgodovina] = useState<string[]>([]);
  const [urejamPlacilo, setUrejamPlacilo] = useState<any>(null);
  const [iskanje, setIskanje] = useState('');
  // [PATCH placila-filter v1] — filter: katere račune plačati
  const [fRokOd, setFRokOd] = useState('');
  const [fRokDo, setFRokDo] = useState('');
  const [fDob, setFDob] = useState('');
  const [fZnOd, setFZnOd] = useState('');
  const [fZnDo, setFZnDo] = useState('');
  // [PATCH placila-interna-vsota v1] filter Interna od-do
  const [fIntOd, setFIntOd] = useState('');
  const [fIntDo, setFIntDo] = useState('');
  // [PATCH placila-filter v2] način datuma izvršitve SEPA; pri "isti dan" je vnos zneskov blokiran brez datuma
  const [nacinDatuma, setNacinDatuma] = useState<'valuta' | 'izbrani'>('valuta');
  const [datumIzvrsitve, setDatumIzvrsitve] = useState('');
  const datumManjka = nacinDatuma === 'izbrani' && !datumIzvrsitve;
  const [sortBy, setSortBy] = useState('rok');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [urejam, setUrejam] = useState<any>(null);
  const [sepaConfirm, setSepaConfirm] = useState(false);
  const [pdfRow, setPdfRow] = useState<string | null>(null);
  const [pdfVNovem, setPdfVNovem] = useState(false);
  const [pdfWidth, setPdfWidth] = useState<number>(() => {
    const v = parseInt(localStorage.getItem('aicount_placila_pdf_width') || '600', 10);
    return isNaN(v) ? 600 : v;
  });
  const pdfWidthRef = useRef(pdfWidth);
  const pdfPanelRef = useRef<HTMLDivElement>(null);

  const nalozi = () => {
    if (!baza) return;
    api.get('/stranka/' + baza.id + '/racuni')
      .then((r: any) => { setRacuni(r.data); setVnosi({}); })
      .catch(() => toast.error('Napaka pri nalaganju računov'));
    api.get('/companies/' + baza.id + '/ibani')
      .then((r: any) => {
        setIbani(r.data.ibani || []);
        setIzbranIban((prev: string) => (r.data.ibani || []).includes(prev) ? prev : ((r.data.ibani || [])[0] || ''));
      }).catch(() => {});
  };
  useEffect(nalozi, [baza]);

  const neplacani = racuni.filter((r) => Number(r.odprto) > 0);
  const placani = racuni.filter((r) => Number(r.placano_znesek) > 0 || Number(r.pobot) > 0);
  const naloziSplacilom = neplacani.filter((r) => num(vnosi[r.id]) > 0);
  const vsotaVnesenih = naloziSplacilom.reduce((s, r) => s + num(vnosi[r.id]), 0);
  const vneseniBrezIban = naloziSplacilom.filter((r) => !r.ima_iban);  // [PATCH placila-interna-vsota v1] SEPA ob enem samem manjkajočem IBAN zavrne CEL paket

  const prikazani = useMemo(() => {
    const osnova = racuni.filter((r) =>
      zavihek === 'neplacani' ? Number(r.odprto) > 0
        : (Number(r.placano_znesek) > 0 || Number(r.pobot) > 0));
    const q = iskanje.trim().toLowerCase();
    const f = q ? osnova.filter((r) => [
      r.interna_st, r.dobavitelj, r.stevilka, r.rok_placila, r.datum,
      r.znesek, r.iban, r.sklic, r.opomba, fmtDatum(r.rok_placila), fmtDatum(r.datum),
    ].some((x) => String(x ?? '').toLowerCase().includes(q))) : osnova;
    const smer = sortDir === 'asc' ? 1 : -1;
    const val = (r: any): any => {
      switch (sortBy) {
        case 'interna_st': return Number(r.interna_st || 0);
        case 'dobavitelj': return String(r.dobavitelj || '').toLowerCase();
        case 'stevilka': return String(r.stevilka || '').toLowerCase();
        case 'rok': return r.rok_placila || '9999-12-31';
        case 'datum': return r.datum || '9999-12-31';
        case 'znesek': return num(r.znesek);
        case 'odprto': return Number(r.odprto || 0);
        case 'placano_znesek': return Number(r.placano_znesek || 0);
        default: return 0;
      }
    };
    return [...f].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -smer;
      if (va > vb) return smer;
      return 0;
    });
  }, [racuni, zavihek, iskanje, sortBy, sortDir]);

  const sortiraj = (col: string) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };
  const sIk = (col: string) => (sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const preklopiZgodovino = (id: string) =>
    setOdprtaZgodovina(odprtaZgodovina.includes(id)
      ? odprtaZgodovina.filter((x) => x !== id) : [...odprtaZgodovina, id]);

  const pripraviZnesek = (r: any) => {
    if (jeNelikvidiran(r)) { toast.error('Račun še ni likvidiran — plačilo ni mogoče.'); return; }  /* [PATCH likvidacija-fe v1] */
    const poln = eur(r.odprto);
    setVnosi({ ...vnosi, [r.id]: (vnosi[r.id] === poln ? '' : poln) });
  };

  // [PATCH placila-filter v1] — logika filtra: zadetki na osnovi Odprto,
  // polnjenje preskoči vrstice brez IBAN (SEPA sicer zavrne CEL paket),
  // račun brez roka ob aktivnem datumskem filtru NI zajet (konservativno).
  const filterAktiven = !!(fRokOd || fRokDo || fDob.trim() || fZnOd.trim() || fZnDo.trim() || fIntOd.trim() || fIntDo.trim());  // [PATCH placila-interna-vsota v1]
  const filterZadetki = !filterAktiven ? ([] as any[]) : neplacani.filter((r) => {
    const rok = String(r.rok_placila || '').slice(0, 10);
    if (fRokOd && (!rok || rok < fRokOd)) return false;
    if (fRokDo && (!rok || rok > fRokDo)) return false;
    const q = fDob.trim().toLowerCase();
    if (q && !String(r.dobavitelj || '').toLowerCase().includes(q)) return false;
    const odprto = Number(r.odprto);
    if (fZnOd.trim() && odprto < num(fZnOd) - 0.005) return false;
    if (fZnDo.trim() && odprto > num(fZnDo) + 0.005) return false;
    // [PATCH placila-interna-vsota v1] Interna od-do; vrstica brez interne št. ob aktivnem intervalu NI zajeta (konservativno, kot rok)
    const _iOd = parseInt(fIntOd, 10), _iDo = parseInt(fIntDo, 10);
    const _ist = (r.interna_st === null || r.interna_st === undefined) ? null : Number(r.interna_st);
    if (!isNaN(_iOd) && (_ist === null || _ist < _iOd)) return false;
    if (!isNaN(_iDo) && (_ist === null || _ist > _iDo)) return false;
    return true;
  });
  const filterBrezIban = filterZadetki.filter((r) => !r.ima_iban);
  // [PATCH likvidacija-fe v1] nelikvidiran racun (r.likvidiran === false) se ne placa: polnjenje ga preskoci, SEPA ga zavrne
  const jeNelikvidiran = (r: any) => r?.likvidiran === false;
  const filterNelikvidirani = filterZadetki.filter((r) => r.ima_iban && jeNelikvidiran(r));
  const filterZaPolnjenje = filterZadetki.filter((r) => r.ima_iban && !jeNelikvidiran(r));
  const filterVsota = filterZaPolnjenje.reduce((s, r) => s + Number(r.odprto), 0);

  const napolniIzFiltra = () => {
    if (datumManjka) { toast.error('Najprej vpišite datum plačil'); return; }  // [PATCH placila-filter v2]
    if (filterZaPolnjenje.length === 0) { toast.error('Noben račun (z IBAN) ne ustreza filtru'); return; }
    const novi: any = { ...vnosi };
    filterZaPolnjenje.forEach((r) => { novi[r.id] = eur(r.odprto); });
    setVnosi(novi);
    toast.success(filterZaPolnjenje.length + ' plačil napolnjenih (' + eur(filterVsota) + ' €)' +
      (filterBrezIban.length > 0 ? ' — ' + filterBrezIban.length + ' preskočenih (ni IBAN)' : '') +
      (filterNelikvidirani.length > 0 ? ' — ' + filterNelikvidirani.length + ' preskočenih (ni likvidiran)' : ''));  /* [PATCH likvidacija-fe v1] */
  };

  const pocistiFilter = () => {
    setFRokOd(''); setFRokDo(''); setFDob(''); setFZnOd(''); setFZnDo(''); setFIntOd(''); setFIntDo('');  // [PATCH placila-interna-vsota v1]
    setVnosi({});
  };

  const oznaciVnesene = async () => {
    if (naloziSplacilom.length === 0) { toast.error('Vpišite znesek za plačilo'); return; }
    for (const r of naloziSplacilom) {
      try {
        await api.post('/stranka/' + baza.id + '/racuni/' + r.id + '/placila',
          { znesek: num(vnosi[r.id]), datum: danes() });
      } catch { }
    }
    toast.success('Računi označeni kot plačani (do vpisanega zneska).');
    nalozi();
  };

  const izbrisiPlacilo = async (pid: string) => {
    try {
      await api.delete('/stranka/' + baza.id + '/placila/' + pid);
      toast.success('Plačilo izbrisano');
      nalozi();
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Napaka'); }
  };

  const shraniPlacilo = async () => {
    try {
      await api.put('/stranka/' + baza.id + '/placila/' + urejamPlacilo.id,
        { znesek: num(urejamPlacilo.znesek), datum: urejamPlacilo.datum });
      toast.success('Plačilo posodobljeno');
      setUrejamPlacilo(null);
      nalozi();
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Napaka'); }
  };

  const odpriUpn = async (id: string) => {
    try {
      const r: any = await api.get('/stranka/' + baza.id + '/racuni/' + id + '/upn');
      setUpn({ ...r.data, _docId: id });
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'UPN ni na voljo'); }
  };

  const odpriPDF = async (r: any) => {
    if (!r.ima_prilogo) return;
    setPdfRow(r.id);
    if (pdfVNovem) {
      // PDF je v ločenem oknu -> samo zamenjaj vsebino okna (storage event)
      try {
        const res: any = await api.get('/dokumenti/' + baza.id + '/' + r.id + '/pdf-token');
        localStorage.setItem('aicount_pdf_doc', JSON.stringify({ companyId: baza.id, docId: r.id, token: res.data.token }));
      } catch (e) { /* okno morda zaprto */ }
    }
  };

  const odpriPdfNovoOkno = async (id: string) => {
    try {
      const res: any = await api.get('/dokumenti/' + baza.id + '/' + id + '/pdf-token');
      localStorage.setItem('aicount_pdf_doc', JSON.stringify({ companyId: baza.id, docId: id, token: res.data.token }));
      window.open('/pdf-viewer', 'aicount_pdf_window', 'width=900,height=1100');
      setPdfVNovem(true);
    } catch (e: any) { toast.error('Priloga ni na voljo'); }
  };

  // Drag-resize PDF panela (panel je desno: vlečenje levo = širši)
  const onPdfResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = pdfWidthRef.current;
    document.querySelectorAll('iframe').forEach((f: any) => (f.style.pointerEvents = 'none'));
    document.body.style.cursor = 'col-resize';
    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const newW = Math.max(340, Math.min(1100, startW - dx));
      pdfWidthRef.current = newW;
      if (pdfPanelRef.current) pdfPanelRef.current.style.width = newW + 'px';
    };
    const onUp = () => {
      document.querySelectorAll('iframe').forEach((f: any) => (f.style.pointerEvents = ''));
      document.body.style.cursor = '';
      setPdfWidth(pdfWidthRef.current);
      localStorage.setItem('aicount_placila_pdf_width', String(pdfWidthRef.current));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Počisti PDF okno ob zapustitvi strani
  useEffect(() => () => { localStorage.removeItem('aicount_pdf_doc'); }, []);

  const oznaciPlacano = async (id: string, zapriUpn = false) => {
    const _r = racuni.find((x: any) => x.id === id);  /* [PATCH likvidacija-fe v1] */
    if (_r && jeNelikvidiran(_r)) { toast.error('Račun še ni likvidiran — plačila ni mogoče označiti.'); return; }
    try {
      await api.post('/stranka/' + baza.id + '/racuni/' + id + '/placano', { placano: true });
      toast.success('Račun označen kot plačan');
      if (zapriUpn) setUpn(null);
      nalozi();
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Napaka'); }
  };

  const shraniPolje = async (r: any, p2: 'iban' | 'sklic', vrednost: string) => {
    try {
      await api.put('/stranka/' + baza.id + '/racuni/' + r.id, { [p2]: vrednost });
      setUrejam(null);
      nalozi();
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Napaka'); }
  };

  const prenesiSepa = async (oznaci: boolean) => {
    setSepaConfirm(false);
    const ids = naloziSplacilom.map((r) => r.id);
    const zneski: any = {};
    naloziSplacilom.forEach((r) => { zneski[r.id] = num(vnosi[r.id]); });
    if (ids.length === 0) { toast.error('Vpišite znesek za plačilo'); return; }
    if (!izbranIban) { toast.error('Izberite IBAN plačnika'); return; }
    if (datumManjka) { toast.error('Najprej vpišite datum plačil'); return; }
    try {
      const r: any = await api.post('/stranka/' + baza.id + '/sepa',
        { doc_ids: ids, placnik_iban: izbranIban, zneski,
          nacin_datuma: nacinDatuma,  // [PATCH placila-filter v2]
          datum_izvrsitve: nacinDatuma === 'izbrani' ? datumIzvrsitve : null },
        { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'sepa_paket.xml'; a.click();
      URL.revokeObjectURL(url);
      // [PATCH likvidacija-fe v1] streznik nelikvidirane preskoci in jih navede v glavah odgovora
      const _stPresk = Number(r.headers?.['x-preskoceni-nelikvidirani'] || 0);
      if (_stPresk > 0) {
        let _stev = ''; try { _stev = decodeURIComponent(r.headers?.['x-preskoceni-stevilke'] || ''); } catch { _stev = ''; }
        toast.error('V paket ni šlo ' + _stPresk + ' nelikvidiranih računov' + (_stev ? ': ' + _stev.slice(0, 120) : ''), { duration: 8000 });
      }
      if (oznaci) { await oznaciVnesene(); }
      else { toast.success('SEPA paket prenesen — uvozite ga v spletno banko.'); }
    } catch (e: any) {
      const detail = e?.response?.data instanceof Blob
        ? await e.response.data.text() : e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail.slice(0, 150) : 'Napaka pri SEPA paketu');
    }
  };

  const shraniIbane = async (seznam: string[]) => {
    try {
      const r: any = await api.put('/companies/' + baza.id + '/ibani', { ibani: seznam });
      setIbani(r.data.ibani);
      if (!r.data.ibani.includes(izbranIban)) setIzbranIban(r.data.ibani[0] || '');
      toast.success('IBAN-i shranjeni');
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Napaka'); }
  };

  const polje = 'px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm';
  const zavCls = (z: string) =>
    'px-5 py-2 rounded-lg text-sm font-semibold transition ' +
    (zavihek === z ? 'bg-blue-600 text-white shadow'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600');
  const thS = (col: string, label: string, align: string = 'left') => (
    <th className={'px-3 py-3 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 ' +
        (align === 'right' ? 'text-right' : 'text-left')}
        onClick={() => sortiraj(col)}>{label}{sIk(col)}</th>
  );

  const urejPolje = (r: any, p2: 'iban' | 'sklic', label: string) => {
    const aktiven = urejam?.id === r.id && urejam?.polje === p2;
    const vrednost = p2 === 'iban' ? r.iban : r.sklic;
    if (aktiven) {
      return (
        <div className="flex items-center gap-1">
          <input autoFocus value={urejam.vrednost}
            onChange={(e) => setUrejam({ ...urejam, vrednost: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') shraniPolje(r, p2, urejam.vrednost);
              if (e.key === 'Escape') setUrejam(null);
            }}
            placeholder={p2 === 'iban' ? 'SI56 ...' : 'SI00 ...'}
            className="w-40 px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs font-mono" />
          {p2 === 'iban' && r.partner_iban && (
            <button type="button" title={'Iz šifranta: ' + r.partner_iban}
              onClick={() => setUrejam({ ...urejam, vrednost: r.partner_iban })}
              className="text-[11px] text-blue-600 hover:underline whitespace-nowrap">šifrant</button>
          )}
          <button onClick={() => shraniPolje(r, p2, urejam.vrednost)} className="text-green-600 font-bold text-sm">✓</button>
          <button onClick={() => setUrejam(null)} className="text-gray-400 text-sm">✕</button>
        </div>
      );
    }
    return (
      <button onClick={() => setUrejam({ id: r.id, polje: p2, vrednost: vrednost || '' })}
        title={'Uredi ' + label}
        className="font-mono text-xs text-left text-gray-700 dark:text-gray-300 hover:underline decoration-dotted">
        <span className="text-gray-400 mr-1">{label}:</span>{vrednost || '—'}
      </button>
    );
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Plačila</h1>
        {!baza && <span className="text-sm text-gray-400">Izberite podjetje v glavi zgoraj.</span>}
      </div>

      <div className="flex gap-2 mb-4">
        <button className={zavCls('neplacani')} onClick={() => setZavihek('neplacani')}>
          Neplačani ({neplacani.length})
        </button>
        <button className={zavCls('placani')} onClick={() => setZavihek('placani')}>
          Plačani ({placani.length})
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div className="flex-1 min-w-[220px]">
          <input value={iskanje} onChange={(e) => setIskanje(e.target.value)}
            placeholder="Iskanje po vseh poljih …" className={polje + ' w-full'} />
        </div>
        {zavihek === 'neplacani' && (
          <>
            <button onClick={oznaciVnesene} disabled={naloziSplacilom.length === 0}
              className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium disabled:opacity-40">
              Označi za plačano{naloziSplacilom.length > 0 ? ' (' + naloziSplacilom.length + ' / ' + eur(vsotaVnesenih) + ' €)' : ''}
            </button>
            <div className="ml-auto flex items-end gap-2">
              {/* [PATCH placila-interna-vsota v1] vidna vsota izbranega = točna vsebina SEPA paketa (naloziSplacilom / vsotaVnesenih) */}
              <div className={'px-4 py-1 rounded-lg border-2 text-right ' + (naloziSplacilom.length > 0
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/25'
                  : 'border-gray-300 dark:border-gray-600 opacity-60')}>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">Za plačilo ({naloziSplacilom.length})</div>
                <div className={'text-xl font-bold tabular-nums leading-tight whitespace-nowrap ' + (naloziSplacilom.length > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400')}>{eur(vsotaVnesenih)} €</div>
                {vneseniBrezIban.length > 0 && (
                  <div className="text-[11px] text-red-500 font-semibold whitespace-nowrap">{vneseniBrezIban.length} brez IBAN — paket bo zavrnjen</div>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">IBAN plačnika</label>
                <select value={izbranIban} onChange={(e) => setIzbranIban(e.target.value)} className={polje + ' w-72'}>
                  {ibani.length === 0 && <option value="">— ni IBAN-ov —</option>}
                  {ibani.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <button onClick={() => {
                  if (naloziSplacilom.length === 0) { toast.error('Vpišite znesek za plačilo'); return; }
                  if (!izbranIban) { toast.error('Izberite IBAN plačnika'); return; }
                  if (datumManjka) { toast.error('Najprej vpišite datum plačil'); return; }
                  setSepaConfirm(true);
                }}
                disabled={naloziSplacilom.length === 0 || !izbranIban || datumManjka}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-40">
                Prenesi SEPA paket
              </button>
            </div>
          </>
        )}
      </div>

      {/* [PATCH placila-filter v1] — filter: katere račune plačati */}
      {zavihek === 'neplacani' && (
        <div className="flex flex-wrap items-end gap-3 mb-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Rok od</label>
            <DatumVnos value={fRokOd} onChange={setFRokOd} className={polje + ' w-28'} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Rok do</label>
            <DatumVnos value={fRokDo} onChange={setFRokDo} className={polje + ' w-28'} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Dobavitelj</label>
            <input value={fDob} onChange={(e) => setFDob(e.target.value)} placeholder="del imena …" className={polje + ' w-44'} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Odprto od €</label>
            <input value={fZnOd} onChange={(e) => setFZnOd(e.target.value)} placeholder="0,00" className={polje + ' w-24 text-right'} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Odprto do €</label>
            <input value={fZnDo} onChange={(e) => setFZnDo(e.target.value)} placeholder="0,00" className={polje + ' w-24 text-right'} />
          </div>
          {/* [PATCH placila-interna-vsota v1] filter Interna od-do */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Interna od</label>
            <input value={fIntOd} onChange={(e) => setFIntOd(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="št." className={polje + ' w-20 text-right'} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Interna do</label>
            <input value={fIntDo} onChange={(e) => setFIntDo(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="št." className={polje + ' w-20 text-right'} />
          </div>
          <button onClick={napolniIzFiltra} disabled={!filterAktiven || filterZaPolnjenje.length === 0 || datumManjka}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-40">
            Napolni plačila{filterAktiven && filterZaPolnjenje.length > 0 ? ' (' + filterZaPolnjenje.length + ' / ' + eur(filterVsota) + ' €)' : ''}
          </button>
          <button onClick={pocistiFilter}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm">
            Počisti
          </button>
          {filterAktiven && filterBrezIban.length > 0 && (
            <span className="text-xs text-amber-600 self-center">{filterBrezIban.length} brez IBAN — bodo preskočeni</span>
          )}
          {/* [PATCH placila-filter v2] način datuma izvršitve plačil */}
          <div className="ml-auto flex items-end gap-2">
            <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
              <button type="button" onClick={() => setNacinDatuma('valuta')}
                className={'px-3 py-2 text-xs font-medium ' + (nacinDatuma === 'valuta'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600')}>
                Vsako plačilo na svoj datum valute
              </button>
              <button type="button" onClick={() => setNacinDatuma('izbrani')}
                className={'px-3 py-2 text-xs font-medium border-l border-gray-300 dark:border-gray-600 ' + (nacinDatuma === 'izbrani'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600')}>
                Vsa plačila na isti dan
              </button>
            </div>
            {nacinDatuma === 'izbrani' && (
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Datum plačil</label>
                <DatumVnos value={datumIzvrsitve} onChange={setDatumIzvrsitve} zahtevan className={polje + ' w-28 text-right'} />
              </div>
            )}
          </div>
        </div>
      )}

      {pdfVNovem && (
        <div className="mb-2 flex justify-end">
          <button onClick={() => setPdfVNovem(false)} className="text-xs text-blue-500 hover:underline flex items-center gap-1">← PDF nazaj</button>
        </div>
      )}
      <div className="flex gap-0 h-[calc(100vh-210px)]">
        <div className="flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-xl shadow overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            {zavihek === 'neplacani' ? (
              <tr>
                {thS('interna_st', 'Interna')}
                {thS('dobavitelj', 'Dobavitelj')}
                {thS('stevilka', 'Številka')}
                {thS('rok', 'Rok')}
                {thS('znesek', 'Znesek', 'right')}
                {thS('placano_znesek', 'Plačano', 'right')}
                {thS('odprto', 'Odprto', 'right')}
                <th className="px-3 py-3 text-left">IBAN / Sklic</th>
                <th className="px-3 py-3">Plačilo</th>
                <th className="px-3 py-3"></th>
              </tr>
            ) : (
              <tr>
                {thS('interna_st', 'Interna')}
                {thS('dobavitelj', 'Dobavitelj')}
                {thS('stevilka', 'Številka')}
                {thS('datum', 'Datum')}
                {thS('znesek', 'Znesek', 'right')}
                {thS('odprto', 'Odprto', 'right')}
                <th className="px-3 py-3 text-left">Pripravljeno plačilo</th>
              </tr>
            )}
          </thead>
          <tbody>
            {zavihek === 'neplacani' && prikazani.map((r) => {
              const odprto = Number(r.odprto);
              const placano = Number(r.placano_znesek);
              return (
              <React.Fragment key={r.id}>
              {/* [PATCH placila-interna-vsota v2] vrstica brez IBAN rahlo rdeča — Napolni/SEPA jo preskočita oz. zavrneta */}
              <tr className={'border-b border-gray-100 dark:border-gray-700' + (!r.ima_iban ? ' bg-red-50 dark:bg-red-900/25' : '')}>
                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{r.interna_st ?? '—'}</td>
                <td onClick={() => odpriPDF(r)} className={'px-3 py-2 font-medium text-gray-900 dark:text-white ' + (r.ima_prilogo ? 'cursor-pointer hover:text-blue-600' : '')}>{r.dobavitelj}</td>
                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{r.stevilka || '—'}{r.ima_prilogo && <button onClick={() => odpriPDF(r)} title="Priloga" className="ml-2 text-blue-600 hover:underline">📎</button>}</td>
                <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtDatum(r.rok_placila)}</td>
                <td className="px-3 py-2 text-right text-gray-900 dark:text-white">{eur(r.znesek)}</td>
                <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">
                  {placano > 0
                    ? <button onClick={() => preklopiZgodovino(r.id)} className="underline decoration-dotted" title="Zgodovina plačil">{eur(placano)}</button>
                    : '—'}
                  {Number(r.pobot) > 0 && <div className="text-[11px] text-emerald-600 dark:text-emerald-400" title={r.pobot_opis || ''}>pobot {eur(r.pobot)} €</div>}
                </td>
                <td className="px-3 py-2 text-right font-semibold text-amber-600">{eur(odprto)}{jeNelikvidiran(r) && <div className="text-[10px] font-semibold text-orange-600 dark:text-orange-400" title="Stranka računa še ni likvidirala">nelikvidiran</div>}{/* [PATCH likvidacija-fe v1] */}</td>
                <td className="px-3 py-2">
                  <div className="space-y-0.5">
                    <div>{urejPolje(r, 'iban', 'IBAN')}</div>
                    <div>{urejPolje(r, 'sklic', 'Sklic')}</div>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <input value={vnosi[r.id] ?? ''} placeholder="0,00" disabled={datumManjka || jeNelikvidiran(r)}
                      title={jeNelikvidiran(r) ? 'Račun še ni likvidiran' : datumManjka ? 'Najprej vpišite datum plačil' : undefined}  /* [PATCH likvidacija-fe v1] */
                      onChange={(e) => setVnosi({ ...vnosi, [r.id]: e.target.value })}
                      className="w-24 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-right text-sm disabled:opacity-40" />
                    <button onClick={() => pripraviZnesek(r)} disabled={datumManjka || jeNelikvidiran(r)} title={jeNelikvidiran(r) ? 'Račun še ni likvidiran' : 'Prepiši odprti znesek (ponovni klik počisti)'}
                      className="w-7 h-7 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 font-bold disabled:opacity-40">+</button>
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {r.ima_iban && (
                    <button onClick={() => odpriUpn(r.id)} className="text-blue-600 hover:underline">UPN QR</button>)}
                </td>
              </tr>
              {odprtaZgodovina.includes(r.id) && (r.placila || []).length > 0 && (
                <tr className="bg-gray-50 dark:bg-gray-900/40">
                  <td colSpan={10} className="px-3 py-2">
                    {(r.placila || []).map((p: any) => (
                      <div key={p.id} className="flex items-center gap-4 py-1 text-sm">
                        <span className="text-gray-600 dark:text-gray-300 w-28">{fmtDatum(p.datum)}</span>
                        <span className="font-medium text-gray-900 dark:text-white w-28 text-right">{eur(p.znesek)} €</span>
                        <button onClick={() => setUrejamPlacilo({ ...p, racun: r.dobavitelj })} className="text-blue-600 hover:underline">Popravi</button>
                        <button onClick={() => izbrisiPlacilo(p.id)} className="text-red-500 hover:underline">Izbriši</button>
                      </div>
                    ))}
                  </td>
                </tr>
              )}
              </React.Fragment>
            );})}

            {zavihek === 'placani' && prikazani.map((r) => (
              <tr key={r.id} className="border-b border-gray-100 dark:border-gray-700 align-top">
                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{r.interna_st ?? '—'}</td>
                <td onClick={() => odpriPDF(r)} className={'px-3 py-2 font-medium text-gray-900 dark:text-white ' + (r.ima_prilogo ? 'cursor-pointer hover:text-blue-600' : '')}>{r.dobavitelj}</td>
                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{r.stevilka || '—'}{r.ima_prilogo && <button onClick={() => odpriPDF(r)} title="Priloga" className="ml-2 text-blue-600 hover:underline">📎</button>}</td>
                <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtDatum(r.datum)}</td>
                <td className="px-3 py-2 text-right font-semibold text-green-600">{eur(r.znesek)}</td>
                <td className={"px-3 py-2 text-right font-semibold " + (Number(r.odprto) > 0.005 ? "text-amber-600" : "text-green-600")}>{eur(r.odprto)}</td>
                <td className="px-3 py-2">
                  {Number(r.pobot) > 0 && (
                    <div className="text-sm text-emerald-600 dark:text-emerald-400 mb-0.5">🔗 Poravnano: {r.pobot_opis || 'dobropis/avans'} (-{eur(r.pobot)} €)</div>
                  )}
                  {(r.placila || []).map((p: any) => (
                    <div key={p.id} className="flex items-center gap-4 py-0.5 text-sm">
                      <span className="text-gray-600 dark:text-gray-300 w-28">{fmtDatum(p.datum)}</span>
                      <span className="font-medium text-gray-900 dark:text-white w-28 text-right">{eur(p.znesek)} €</span>
                      <button onClick={() => setUrejamPlacilo({ ...p, racun: r.dobavitelj })} className="text-blue-600 hover:underline">Popravi</button>
                      <button onClick={() => izbrisiPlacilo(p.id)} className="text-red-500 hover:underline">Izbriši</button>
                    </div>
                  ))}
                </td>
              </tr>
            ))}

            {prikazani.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-gray-400">
                {zavihek === 'neplacani' ? 'Ni odprtih računov.' : 'Ni plačanih računov.'}</td></tr>)}
          </tbody>
        </table>
        </div>
        {pdfRow && !pdfVNovem && (
          <div onMouseDown={onPdfResize}
            className="w-1.5 mx-1 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 rounded-full transition-colors flex-shrink-0 self-stretch bg-gray-200 dark:bg-gray-700" />
        )}
        {pdfRow && !pdfVNovem && (
          <div ref={pdfPanelRef} className="flex-shrink-0 flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden" style={{ width: pdfWidth + 'px' }}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">PDF Predogled</span>
              <div className="flex items-center gap-3">
                <button onClick={() => odpriPdfNovoOkno(pdfRow)} className="text-xs text-blue-500 hover:underline">↗ Odpri v novem oknu</button>
                <button onClick={() => setPdfRow(null)} className="text-xs text-gray-400 hover:underline">✕ Zapri</button>
              </div>
            </div>
            <div className="flex-1 min-h-0"><PdfViewer key={pdfRow} companyId={baza.id} docId={pdfRow} /></div>
          </div>
        )}
      </div>

      {sepaConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 text-center">
            <h3 className="font-bold text-gray-900 dark:text-white mb-2">SEPA paket</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Paket vsebuje {naloziSplacilom.length} {naloziSplacilom.length === 1 ? 'račun' : 'računov'} ({eur(vsotaVnesenih)} €).<br />
              Račune označim za plačane?
            </p>
            <div className="flex gap-3">
              <button onClick={() => prenesiSepa(false)}
                className="flex-1 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm">Samo prenesi paket</button>
              <button onClick={() => prenesiSepa(true)}
                className="flex-1 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold">Označi za plačane</button>
            </div>
            <button onClick={() => setSepaConfirm(false)} className="mt-3 text-xs text-gray-400 hover:underline">Prekliči</button>
          </div>
        </div>
      )}

      {urejamIbane && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3">IBAN-i podjetja {baza?.name}</h3>
            {ibani.map((i) => (
              <div key={i} className="flex items-center justify-between py-1 text-sm">
                <span className="text-gray-700 dark:text-gray-200 font-mono">{i}</span>
                <button onClick={() => shraniIbane(ibani.filter((x) => x !== i))} className="text-red-500 hover:underline">Odstrani</button>
              </div>
            ))}
            {ibani.length === 0 && <p className="text-sm text-gray-400 py-2">Ni vpisanih IBAN-ov.</p>}
            <div className="flex gap-2 mt-3">
              <input value={novIban} onChange={(e) => setNovIban(e.target.value)} placeholder="SI56 ..." className={polje + ' flex-1'} />
              <button onClick={() => { if (novIban.trim()) { shraniIbane([...ibani, novIban]); setNovIban(''); } }}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold">+</button>
            </div>
            <button onClick={() => setUrejamIbane(false)}
              className="mt-4 w-full py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm">Zapri</button>
          </div>
        </div>
      )}

      {urejamPlacilo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3">Popravi plačilo — {urejamPlacilo.racun}</h3>
            <label className="block text-xs text-gray-500 mb-1">Znesek €</label>
            <input className={polje + ' w-full mb-3'} value={urejamPlacilo.znesek}
              onChange={(e) => setUrejamPlacilo({ ...urejamPlacilo, znesek: e.target.value })} />
            <label className="block text-xs text-gray-500 mb-1">Datum plačila</label>
            <input type="date" className={polje + ' w-full mb-4'} value={urejamPlacilo.datum}
              onChange={(e) => setUrejamPlacilo({ ...urejamPlacilo, datum: e.target.value })} />
            <div className="flex gap-3">
              <button onClick={() => setUrejamPlacilo(null)}
                className="flex-1 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">Prekliči</button>
              <button onClick={shraniPlacilo}
                className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold">Shrani</button>
            </div>
          </div>
        </div>
      )}

      {upn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setUpn(null)}>
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900 mb-1">Plačilo s QR kodo</h3>
            <p className="text-sm text-gray-500 mb-4">Skenirajte z aplikacijo svoje mobilne banke</p>
            <div className="flex justify-center mb-4">
              <QRCodeSVG value={upn.upnqr} size={230} level="M" />
            </div>
            <div className="text-sm text-left text-gray-700 space-y-1 mb-4">
              <div><b>Prejemnik:</b> {upn.prejemnik}</div>
              <div><b>IBAN:</b> {upn.iban}</div>
              {upn.bic && <div><b>BIC:</b> {upn.bic}</div>}
              <div><b>Sklic:</b> {upn.sklic}</div>
              <div><b>Znesek (odprti):</b> {eur(upn.znesek)} €</div>
              {upn.rok && <div><b>Rok plačila:</b> {upn.rok}</div>}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setUpn(null)}
                className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm">Zapri</button>
              <button onClick={() => oznaciPlacano(upn._docId, true)} disabled={jeNelikvidiran(racuni.find((x: any) => x.id === upn._docId))}
                title={jeNelikvidiran(racuni.find((x: any) => x.id === upn._docId)) ? 'Račun še ni likvidiran' : undefined}
                className="flex-1 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed">Plačano</button>{/* [PATCH likvidacija-fe v1] */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
